import { useEffect, useState } from "preact/hooks";
import type { FlowState } from "../flow/machine";
import type { InstallRunner } from "../flow/runner";
import { t } from "../i18n";
import { fmtBytes } from "./hooks";

export function FlashStep({ runner, s, connected, autoStart }: { runner: InstallRunner; s: FlowState; connected: boolean; autoStart: boolean }) {
  const [running, setRunning] = useState(false);
  const f = s.flash;
  const phase = f?.phase ?? "manifest";
  const start = async () => {
    setRunning(true);
    await runner.flash();
    setRunning(false);
  };
  // Auto-start once when we arrive here connected (e.g. straight after unlock); a reload requires a click.
  const [started, setStarted] = useState(false);
  useEffect(() => {
    if (autoStart && connected && !started && !s.error && !running) {
      setStarted(true);
      void start();
    }
  }, [autoStart, connected, started]);
  const needsDevice = phase === "flashing" || phase === "locking";
  const pct = f && f.bytesTotal > 0 ? Math.round((f.bytesDone / f.bytesTotal) * 100) : 0;
  return (
    <div class="panel">
      <h2 tabIndex={-1}>{s.mode === "stock" ? t("flash.heading_stock") : t("flash.heading")}</h2>
      {s.buildId && <p class="muted">{t("flash.build", { build: s.buildId })}</p>}
      <p class="notice warn">{t("flash.reboot_note")}</p>
      <ol class="timeline" aria-label={t("steps.flash")}>
        {(["manifest", "download", "flashing", "locking", "done"] as const).map((p, i, all) => {
          const cur = all.indexOf(phase);
          const cls = i < cur ? "done" : i === cur ? "current" : "";
          return (
            <li key={p} class={cls} aria-current={i === cur ? "step" : undefined}>{t(`flash.phase.${p}`)}</li>
          );
        })}
      </ol>
      {phase === "download" && f && (
        <div>
          <div class="progress-label"><span>{t("flash.file_progress", { n: f.fileIndex + 1, total: f.fileCount, name: f.current ?? "" })}</span><span>{t("common.bytes_of", { done: fmtBytes(f.bytesDone), total: fmtBytes(f.bytesTotal) })}</span></div>
          <progress max={100} value={pct} aria-label={t("flash.phase.download")}>{pct}%</progress>
        </div>
      )}
      {phase === "flashing" && f && (
        <div>
          <div class="progress-label"><span>{t("flash.part_progress", { name: f.current ?? "" })}</span><span>{f.partTotal > 0 ? t("common.bytes_of", { done: fmtBytes(f.partDone), total: fmtBytes(f.partTotal) }) : ""}</span></div>
          <progress max={f.partTotal || 1} value={f.partDone} aria-label={t("flash.phase.flashing")} />
        </div>
      )}
      {phase === "locking" && <p class="notice warn">{s.leaveUnlocked ? t("flash.leave_unlocked_note") : t("flash.lock_on_phone")}</p>}
      {!running && phase !== "done" && (
        <div class="row" style="margin-top:1rem">
          {needsDevice && !connected ? (
            <button type="button" class="primary" onClick={() => void runner.reconnect().then(async (ok) => { if (ok) await start(); })}>{t("preflight.reconnect")}</button>
          ) : (
            <button type="button" class="primary" onClick={() => void start()} disabled={!!s.error}>{f && f.bytesDone > 0 ? t("flash.resume") : t("flash.start")}</button>
          )}
        </div>
      )}
    </div>
  );
}
